import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import {
    Archive, Check, ChevronLeft, ChevronRight, CircleAlert, CircleCheck, Code2, Copy,
    FileSpreadsheet, Package, PenLine, Plus, Search, SquarePen, X
} from 'lucide-react';
import { BASE_URL } from '../../../config/config';
import { StorefrontService } from '../../../core/services/storefront.service';
import { ProductForm } from './ProductForm';
import { BulkProductUpload } from './BulkProductUpload';
import { Sheet } from '../../../modules/shared/components/modals/Sheet';
import { Toggle } from '../../../modules/shared/components/settings/SettingsParts';
import '../../../modules/shared/theme/theme.scss';
import '../../../modules/shared/screens/settings/settings.scss';
import './storefront.scss';

interface Props { UserReducer: any; UserID: any; }

const STATUS_FILTERS: [string, string][] = [['active', 'Active'], ['draft', 'Draft'], ['archived', 'Archived'], ['', 'All']];

const CURRENCY_SYMBOLS: Record<string, string> = { INR: '₹', USD: '$', EUR: '€', GBP: '£', AED: 'د.إ' };

const StorefrontProductsComponent: React.FC<Props> = ({ UserReducer }) => {
    const [view, setView] = useState<'list' | 'form' | 'bulk'>('list');
    const [storefront, setStorefront] = useState<any>(null);
    const [storefrontLoading, setStorefrontLoading] = useState(true);
    const [products, setProducts] = useState<any[]>([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(20);
    const [search, setSearch] = useState('');
    const [filterStatus, setFilterStatus] = useState('active');
    const [filterCategory, setFilterCategory] = useState('');
    const [loading, setLoading] = useState(false);
    const [editProduct, setEditProduct] = useState<any>(null);
    const [alert, setAlert] = useState<{ type: 'success' | 'error'; msg: string } | null>(null);
    const [apiDocsOpen, setApiDocsOpen] = useState(false);
    const [keyCopied, setKeyCopied] = useState(false);
    const [curlCopied, setCurlCopied] = useState(false);

    const brand_id = UserReducer?.UserData?.brand_id;

    useEffect(() => {
        StorefrontService.getMyStorefront(brand_id)
            .then((res: any) => { if (res?.data?.rows?.[0]) setStorefront(res.data.rows[0]); })
            .catch(() => {})
            .finally(() => setStorefrontLoading(false));
    }, [brand_id]);

    useEffect(() => {
        if (!storefront) return;
        loadProducts();
    }, [storefront, page, rowsPerPage, filterStatus, filterCategory]);

    const loadProducts = () => {
        setLoading(true);
        StorefrontService.getMyProducts({
            storefront_id: storefront.storefront_id,
            page: page + 1, limit: rowsPerPage,
            status: filterStatus, category: filterCategory, search
        })
            .then((res: any) => {
                setProducts(res?.data?.rows || []);
                setTotal(res?.data?.total || 0);
            })
            .catch(() => {})
            .finally(() => setLoading(false));
    };

    const handleSearch = (e: React.KeyboardEvent) => { if (e.key === 'Enter') loadProducts(); };

    // Optimistic toggle — flips the row immediately, rolls back on failure. Only
    // meaningful while a Affiliate Deals campaign is linked (storefront.cashkaro_campaign_id).
    const handleToggleCampaignFeature = async (product_id: number, next: boolean) => {
        setProducts(prev => prev.map(p => p.product_id === product_id ? { ...p, is_campaign_featured: next ? 1 : 0 } : p));
        try {
            const res = await StorefrontService.toggleCampaignProduct({ storefront_id: storefront.storefront_id, brand_id, product_id, featured: next });
            if (res?.status !== 1) {
                setProducts(prev => prev.map(p => p.product_id === product_id ? { ...p, is_campaign_featured: next ? 0 : 1 } : p));
                setAlert({ type: 'error', msg: res?.info || 'Failed to update campaign selection' });
            }
        } catch {
            setProducts(prev => prev.map(p => p.product_id === product_id ? { ...p, is_campaign_featured: next ? 0 : 1 } : p));
            setAlert({ type: 'error', msg: 'Failed to update campaign selection' });
        }
    };

    const handleArchive = async (product_id: number) => {
        if (!window.confirm('Archive this product?')) return;
        try {
            await StorefrontService.archiveProduct(product_id);
            setAlert({ type: 'success', msg: 'Product archived' });
            loadProducts();
        } catch { setAlert({ type: 'error', msg: 'Failed to archive' }); }
    };

    const sym = (currency: string) => CURRENCY_SYMBOLS[currency] || currency;

    // Manual per-brand flag (see 2026-09-13_brand_deals_permission_and_link_requests.sql)
    // — the "Affiliate Deals" column only renders at all for brands with access, not just
    // disabled, so a brand without it never sees the feature hinted at.
    const brandDealsEnabled = storefront?.brand_deals_enabled === 'True';

    const copyText = (text: string, setter: (v: boolean) => void) => {
        navigator.clipboard.writeText(text).then(() => {
            setter(true);
            setTimeout(() => setter(false), 2000);
        });
    };

    const syncEndpoint = `${BASE_URL}/storefront/products/sync`;
    const apiKey = storefront?.api_key || '';
    const examplePayload = JSON.stringify({
        products: [
            {
                title: "Face Wash 100ml",
                price: 299,
                discount_price: 249,
                currency: "INR",
                category: "Skincare",
                commission_percent: 10,
                product_url: "https://yourbrand.com/face-wash",
                image_urls: ["https://cdn.yourbrand.com/face-wash.jpg"],
                in_stock: true
            },
            {
                sku: "SERUM-001",
                title: "Vitamin C Serum 30ml",
                price: 599,
                currency: "INR",
                category: "Skincare",
                commission_percent: 12,
                product_url: "https://yourbrand.com/vitamin-c-serum",
                image_urls: ["https://cdn.yourbrand.com/serum.jpg"],
                in_stock: true
            }
        ]
    }, null, 2);
    const curlExample = `curl -X POST ${syncEndpoint} \\
  -H "Content-Type: application/json" \\
  -H "X-Api-Key: ${apiKey || '<your-api-key>'}" \\
  -d '{"products": [{"title": "Face Wash", "price": 299, "product_url": "https://...", "currency": "INR"}]}'`;

    if (storefrontLoading) return <div className="brd-center"><span className="brd-spinner" /></div>;

    if (!storefront) return (
        <div className="brd-banner brd-banner--info">
            <CircleAlert size={16} />
            <span>Set up your storefront first. <a className="brd-link" href="/brand/storefront">Go to Setup</a></span>
        </div>
    );

    if (view === 'form') return (
        <ProductForm storefront_id={storefront.storefront_id} product={editProduct}
            cashkaroCampaignId={storefront.cashkaro_campaign_id || null}
            cashkaroCampaignTitle={storefront.cashkaro_campaign_title || null}
            onSaved={() => { setView('list'); setEditProduct(null); loadProducts(); }}
            onCancel={() => { setView('list'); setEditProduct(null); }} />
    );

    if (view === 'bulk') return (
        <BulkProductUpload storefront_id={storefront.storefront_id}
            onDone={() => { setView('list'); loadProducts(); }} />
    );

    const money = (currency: string, value: any) => `${sym(currency)}${parseFloat(value).toFixed(2)}`;
    const statusPill = (status: string) =>
        status === 'active' ? 'brd-pill--success' : status === 'draft' ? 'brd-pill--muted' : 'brd-pill--error';
    const featureHint = storefront.cashkaro_campaign_id
        ? `Feature in "${storefront.cashkaro_campaign_title}"`
        : 'Link an Affiliate Deals campaign in Store Settings first';
    const pageCount = Math.max(1, Math.ceil(total / rowsPerPage));
    const openAdd = () => { setEditProduct(null); setView('form'); };
    const openEdit = (p: any) => { setEditProduct(p); setView('form'); };

    const featureToggle = (p: any) => (
        <span title={featureHint}>
            <Toggle checked={!!p.is_campaign_featured} disabled={!storefront.cashkaro_campaign_id}
                label={`Feature ${p.product_title} in Affiliate Deals`}
                onChange={next => handleToggleCampaignFeature(p.product_id, next)} />
        </span>
    );
    const rowActions = (p: any) => (
        <div className="brd-store-table__actions">
            <button type="button" className="brd-icon-btn" onClick={() => openEdit(p)} title="Edit" aria-label={`Edit ${p.product_title}`}>
                <SquarePen size={16} />
            </button>
            <button type="button" className="brd-icon-btn brd-store-table__archive" onClick={() => handleArchive(p.product_id)} title="Archive" aria-label={`Archive ${p.product_title}`}>
                <Archive size={16} />
            </button>
        </div>
    );

    const addOptions = [
        { icon: <FileSpreadsheet size={20} />, title: 'Bulk upload via CSV', body: 'Upload a CSV with all your products at once. Best for large catalogues — download our template to get started.', cta: 'Upload CSV', onClick: () => setView('bulk') },
        { icon: <PenLine size={20} />, title: 'Add product manually', body: 'Fill in product details one by one — title, price, images, commission and more.', cta: 'Add Product', onClick: openAdd },
        { icon: <Code2 size={20} />, title: 'API integration', body: 'For Shopify / Magento / custom backends. Push products programmatically via our REST API.', cta: 'View API Docs', onClick: () => setApiDocsOpen(true) },
    ];

    return (
        <div className="brd-store-products">
            {/* Toolbar, like Campaigns: status chips + search on the left, actions on the right. */}
            <div className="brd-store-toolbar">
                <div className="brd-chip-scroll" role="tablist" aria-label="Product status">
                    {STATUS_FILTERS.map(([value, label]) => (
                        <button key={label} type="button" role="tab" aria-selected={filterStatus === value}
                            className={`brd-chip${filterStatus === value ? ' brd-chip--on' : ''}`}
                            onClick={() => { setFilterStatus(value); setPage(0); }}>
                            {label}
                        </button>
                    ))}
                </div>
                <div className="brd-store-toolbar__actions">
                    <button type="button" className="brd-btn brd-btn--outline" onClick={() => setApiDocsOpen(true)}><Code2 size={16} /> API Sync</button>
                    <button type="button" className="brd-btn brd-btn--outline" onClick={() => setView('bulk')}><FileSpreadsheet size={16} /> Bulk Upload</button>
                    <button type="button" className="brd-btn brd-btn--primary" onClick={openAdd}><Plus size={16} /> Add Product</button>
                </div>
            </div>

            {alert && (
                <div className={`brd-banner ${alert.type === 'error' ? 'brd-banner--error' : 'brd-banner--info'}`} role="status">
                    {alert.type === 'error' ? <CircleAlert size={16} /> : <CircleCheck size={16} />}
                    <span style={{ flex: 1 }}>{alert.msg}</span>
                    <button type="button" onClick={() => setAlert(null)} aria-label="Dismiss"><X size={14} /></button>
                </div>
            )}

            {/* Empty state — how to add products, before any exist */}
            {!loading && total === 0 && (
                <div className="brd-store-options">
                    {addOptions.map(o => (
                        <div key={o.title} className="brd-card brd-store-option">
                            <span className="brd-store-option__icon">{o.icon}</span>
                            <p className="brd-store-option__title">{o.title}</p>
                            <p className="brd-store-option__body">{o.body}</p>
                            <button type="button" className="brd-btn brd-btn--secondary brd-btn--sm" onClick={o.onClick}>{o.cta}</button>
                        </div>
                    ))}
                </div>
            )}

            <div className="brd-store-filters">
                <label className="brd-search brd-store-filters__search">
                    <Search size={16} />
                    <input value={search} placeholder="Search title or SKU, press Enter" aria-label="Search products"
                        onChange={e => setSearch(e.target.value)} onKeyDown={handleSearch} />
                </label>
                <input className="brd-input brd-store-filters__select" value={filterCategory} placeholder="Category" aria-label="Category"
                    onChange={e => { setFilterCategory(e.target.value); setPage(0); }} />
            </div>

            <div className="brd-card brd-store-table">
                {loading ? (
                    <div className="brd-center"><span className="brd-spinner" /></div>
                ) : products.length === 0 ? (
                    <div className="brd-empty">
                        <span className="brd-empty__icon"><Package size={28} /></span>
                        <p className="brd-empty__title">No products found</p>
                        <p className="brd-empty__body">Try another search or status.</p>
                    </div>
                ) : (
                    <>
                        {/* Tablets and laptops: a table */}
                        <table className="brd-store-table__grid">
                            <thead>
                                <tr>
                                    <th>Product</th>
                                    <th>SKU</th>
                                    <th>Price</th>
                                    <th>Commission</th>
                                    <th>Stock</th>
                                    <th>Status</th>
                                    {brandDealsEnabled && <th title={featureHint}>Affiliate Deals</th>}
                                    <th aria-label="Actions" />
                                </tr>
                            </thead>
                            <tbody>
                                {products.map(p => (
                                    <tr key={p.product_id}>
                                        <td>
                                            <div className="brd-store-table__product">
                                                {p.primary_image ? <img src={p.primary_image} alt="" className="brd-store-table__thumb" /> : <span className="brd-store-table__thumb"><Package size={16} /></span>}
                                                <span className="brd-store-table__title" title={p.product_title}>{p.product_title}</span>
                                            </div>
                                        </td>
                                        <td className="brd-store-table__muted">{p.sku}</td>
                                        <td>
                                            {p.discount_price
                                                ? <><span className="brd-store-table__strike">{money(p.currency, p.price)}</span> {money(p.currency, p.discount_price)}</>
                                                : money(p.currency, p.price)}
                                        </td>
                                        <td>{p.commission_percent}%</td>
                                        <td><span className={`brd-pill ${p.in_stock ? 'brd-pill--success' : 'brd-pill--muted'}`}>{p.in_stock ? 'In stock' : 'Out'}</span></td>
                                        <td><span className={`brd-pill ${statusPill(p.status)}`}>{p.status}</span></td>
                                        {brandDealsEnabled && <td>{featureToggle(p)}</td>}
                                        <td>{rowActions(p)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>

                        {/* Phones: a card per product */}
                        <ul className="brd-store-table__cards">
                            {products.map(p => (
                                <li key={p.product_id} className="brd-store-table__card">
                                    {p.primary_image ? <img src={p.primary_image} alt="" className="brd-store-table__thumb brd-store-table__thumb--lg" /> : <span className="brd-store-table__thumb brd-store-table__thumb--lg"><Package size={18} /></span>}
                                    <div className="brd-store-table__card-text">
                                        <p className="brd-store-table__title">{p.product_title}</p>
                                        <p className="brd-store-table__muted">
                                            {p.discount_price ? money(p.currency, p.discount_price) : money(p.currency, p.price)} · {p.commission_percent}% commission
                                        </p>
                                        <div className="brd-chip-row" style={{ gap: 6, marginTop: 6 }}>
                                            <span className={`brd-pill ${statusPill(p.status)}`}>{p.status}</span>
                                            <span className={`brd-pill ${p.in_stock ? 'brd-pill--success' : 'brd-pill--muted'}`}>{p.in_stock ? 'In stock' : 'Out'}</span>
                                        </div>
                                        {brandDealsEnabled && (
                                            <div className="brd-store-table__card-deal"><span>Affiliate Deals</span>{featureToggle(p)}</div>
                                        )}
                                    </div>
                                    {rowActions(p)}
                                </li>
                            ))}
                        </ul>
                    </>
                )}

                <div className="brd-store-pager">
                    <label className="brd-store-pager__size">
                        Rows
                        <select className="brd-select" value={rowsPerPage} onChange={e => { setRowsPerPage(parseInt(e.target.value)); setPage(0); }}>
                            {[10, 20, 50].map(n => <option key={n} value={n}>{n}</option>)}
                        </select>
                    </label>
                    <span className="brd-store-pager__info">
                        {total ? `${page * rowsPerPage + 1}–${Math.min(total, (page + 1) * rowsPerPage)} of ${total}` : '0 of 0'}
                    </span>
                    <button type="button" className="brd-icon-btn" disabled={page === 0} onClick={() => setPage(page - 1)} aria-label="Previous page"><ChevronLeft size={18} /></button>
                    <button type="button" className="brd-icon-btn" disabled={page + 1 >= pageCount} onClick={() => setPage(page + 1)} aria-label="Next page"><ChevronRight size={18} /></button>
                </div>
            </div>

            {/* API Integration */}
            <Sheet open={apiDocsOpen} onClose={() => setApiDocsOpen(false)} size="wide"
                title="API Integration" subtitle="For Shopify, Magento, or any custom backend — push products programmatically."
                footer={<button type="button" className="brd-btn brd-btn--outline brd-btn--block" onClick={() => setApiDocsOpen(false)}>Close</button>}>
                <div className="brd-store-api">
                    <p className="brd-store-api__label">Your API key</p>
                    <div className="brd-store-api__box">
                        <code>{apiKey || '(no api key yet — save your storefront first)'}</code>
                        {apiKey && (
                            <button type="button" className="brd-icon-btn" onClick={() => copyText(apiKey, setKeyCopied)} title={keyCopied ? 'Copied!' : 'Copy key'} aria-label="Copy API key">
                                {keyCopied ? <Check size={16} /> : <Copy size={16} />}
                            </button>
                        )}
                    </div>

                    <p className="brd-store-api__label">Endpoint</p>
                    <div className="brd-store-api__box"><code><span className="brd-pill brd-pill--primary">POST</span> {syncEndpoint}</code></div>

                    <p className="brd-store-api__label">Authentication</p>
                    <div className="brd-store-api__box"><code>X-Api-Key: {apiKey || '<your-api-key>'}</code></div>

                    <p className="brd-store-api__label">Request body</p>
                    <pre className="brd-store-api__code">{examplePayload}</pre>

                    <p className="brd-store-api__label">Product fields</p>
                    <table className="brd-store-api__fields">
                        <thead><tr><th>Field</th><th>Required</th><th>Description</th></tr></thead>
                        <tbody>
                            {[
                                ['title', 'Yes', 'Product title (also accepts product_title)'],
                                ['price', 'Yes', 'Selling price (numeric)'],
                                ['product_url', 'Yes', 'Full URL of the product on your site'],
                                ['sku', 'No', 'Unique identifier — auto-generated if omitted'],
                                ['discount_price', 'No', 'Discounted price (must be < price)'],
                                ['currency', 'No', 'INR / USD / EUR / GBP (default: INR)'],
                                ['category', 'No', 'Category string'],
                                ['commission_percent', 'No', 'Influencer commission % (default: 0)'],
                                ['image_urls', 'No', 'Array of image URLs (max 5, first = primary)'],
                                ['in_stock', 'No', 'true / false (default: true)'],
                                ['description', 'No', 'Product description text'],
                            ].map(([field, req, desc]) => (
                                <tr key={field}>
                                    <td><code>{field}</code></td>
                                    <td><span className={`brd-pill ${req === 'Yes' ? 'brd-pill--error' : 'brd-pill--muted'}`}>{req}</span></td>
                                    <td>{desc}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>

                    <div className="brd-store-api__label-row">
                        <p className="brd-store-api__label">cURL example</p>
                        <button type="button" className="brd-icon-btn" onClick={() => copyText(curlExample, setCurlCopied)} title={curlCopied ? 'Copied!' : 'Copy cURL'} aria-label="Copy cURL example">
                            {curlCopied ? <Check size={16} /> : <Copy size={16} />}
                        </button>
                    </div>
                    <pre className="brd-store-api__code">{curlExample}</pre>

                    <p className="brd-store-api__label">Response</p>
                    <pre className="brd-store-api__code">{`{ "status": 1, "data": { "inserted": 2, "skipped": 0, "errors": [] } }`}</pre>

                    <div className="brd-banner brd-banner--info" style={{ marginTop: 16, marginBottom: 0 }}>
                        <CircleAlert size={16} />
                        <span><strong>Duplicate SKUs are skipped</strong> — safe to re-sync your full catalogue. Max 5,000 products per request.</span>
                    </div>
                </div>
            </Sheet>
        </div>
    );
};

const mapStateToProps = (state: any) => ({ UserReducer: state.UserReducer, UserID: state.LoginUserIdReducer });
export const StorefrontProducts = connect(mapStateToProps)(StorefrontProductsComponent);
